import { AfterViewInit, OnDestroy } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { forkJoin, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageType } from 'src/app/models/enum/page-type.enum';
import { Item } from 'src/app/models/item.model';
import { Job } from 'src/app/models/job.model';
import { BitwiseHelper } from 'src/app/services/helpers/bitwise.helper';
import { LookupService } from 'src/app/services/lookup.service';
import { JobService } from 'src/app/services/sections/job/job.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';


@Component({
	templateUrl: './comment-follow-up.component.html',
	styleUrls: ['./comment-follow-up.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss']
})
export class CommentFollowUpComponent implements OnInit, OnDestroy, AfterViewInit {

	public formModel: FormGroup;
	public assigneeOptions: Item<number, string>[];
	public followUpTypeOptions: Item<number, string>[];

	public readonly PageType = PageType;
	public readonly ButtonIcons = ButtonIcons;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;
	public readonly BitwiseHelper = BitwiseHelper;

	public pageType: PageType;
	public customerPreferences: SettingsPreferences;

	public job: Job = null;

	public get isFollowUp(): boolean {
		return this.formModel?.get('isFollowUp')?.value;
	}

	public get isActionAvailable(): boolean {
		return !this.customerPreferences?.preferences.isJobAutoSkipped &&
			!(this.pageType === PageType.Customer &&
				this.customerPreferences?.preferences.isNotificationDisabled
			);
	}

	public unsubscriber$ = new Subject<void>();
	public isLoading = false;

	public self = this;

	private readonly ID_GENERAL_TOPIC = 1;

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private lookupService: LookupService,
		private jobService: JobService
	) { }

	ngOnInit(): void {
		this.job = (this.activatedRoute.snapshot.data.jobDetails || {}) as Job;
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences as SettingsPreferences;

		this.pageType = this.activatedRoute.snapshot.data?.pageType;
		this.isLoading = true;

		GlobalSettingsService.isSecondaryLoaderLoading = true;

		this.lookupService.getJobFollowUpTypes()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe((types) => {
				this.followUpTypeOptions = types;

				this.formModel = new FormGroup({
					isFollowUp: new FormControl(false, [Validators.required]),
					topicId: new FormControl(this.ID_GENERAL_TOPIC, [Validators.required]),
					subject: new FormControl(''),
					description: new FormControl(null, [Validators.required, Validators.minLength(10), Validators.pattern(/\S+/)]),
					jobFollowUpTypeId: new FormControl(null),
					dueDate: new FormControl({ value: null, disabled: true })
				});

				this.formModel.get('isFollowUp')
					.valueChanges
					.pipe(takeUntil(this.unsubscriber$))
					.subscribe(value => {
						const followUpDateControl = this.formModel.get('dueDate');
						const isFollowUpControl = this.formModel.get('isFollowUp');

						if (isFollowUpControl.value == null) {
							isFollowUpControl.setValue(false);
						}

						if (value) {
							followUpDateControl.enable();
						} else {
							followUpDateControl.clearValidators();
							followUpDateControl.disable();
							followUpDateControl.reset();
						}
					});

				GlobalSettingsService.isSecondaryLoaderLoading = false;
				this.isLoading = false;
			});
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public submitComment(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;

		const model = this.formModel.getRawValue();
		model.dueDate = model.dueDate ? moment(model.dueDate).format('YYYY-MM-DD') : null;

		this.jobService.addJobComment(this.activatedRoute.snapshot.paramMap.get('token'), model)
			.subscribe(
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					this.router.navigate(['../'], { relativeTo: this.activatedRoute });
				},
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				});
	}
}
